import { useState, useEffect } from 'react';
import {
  Container,
  Tabs,
  Tab,
  Alert,
  Card,
  Button,
  Form,
  Row,
  Col,
  Table,
  Badge,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import { formatUserName } from '../utils/name.util';
import {
  adminService,
  AdminSetting,
  CompanyInfo,
  EmailTemplate,
  EmailHistory,
} from '../services/admin.service';
import { contactService, Contact } from '../services/contact.service';
import { featureRequestService } from '../services/featureRequest.service';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import ConfirmDestructiveModal from '../components/common/ConfirmDestructiveModal';
import { format } from 'date-fns';
import { enUS, ja } from 'date-fns/locale';

const getDateFnsLocaleForAdmin = (lang: string) => {
  switch (lang) {
    case 'ja':
      return ja;
    default:
      return enUS;
  }
};

const getLanguages = (t: (key: string) => string) => [
  { code: 'ja', name: t('common.language_ja') },
  { code: 'en', name: t('common.language_en') },
  { code: 'zh-CN', name: t('common.language_zh_cn') },
  { code: 'fr', name: t('common.language_fr') },
  { code: 'es', name: t('common.language_es') },
  { code: 'de', name: t('common.language_de') },
  { code: 'pt', name: t('common.language_pt') },
  { code: 'ru', name: t('common.language_ru') },
];

export default function AdminSettings() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('settings');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // 管理者設定（一覧は useQuery の data をそのまま利用。v5 では useQuery の onSuccess が使えない）
  const [newSettingKey, setNewSettingKey] = useState('');
  const [newSettingValue, setNewSettingValue] = useState('');
  const [newSettingDescription, setNewSettingDescription] = useState('');
  const [newSettingCategory, setNewSettingCategory] = useState('general');

  // 会社情報
  const [companyInfo, setCompanyInfo] = useState<Partial<CompanyInfo>>({});

  // メールテンプレート
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState<Partial<EmailTemplate>>({});
  const [selectedLanguage, setSelectedLanguage] = useState('ja');
  const [emailTemplateCategoryFilter, setEmailTemplateCategoryFilter] = useState<string | null>(
    null
  );
  const [emailTemplateSearchQuery, setEmailTemplateSearchQuery] = useState('');
  const [emailTemplateStatusFilter, setEmailTemplateStatusFilter] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);

  // メール履歴
  const [emailHistoryPage, setEmailHistoryPage] = useState(1);
  const [selectedHistory, setSelectedHistory] = useState<EmailHistory | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [contactEditStatus, setContactEditStatus] = useState<Contact['status']>('pending');
  const [contactEditNotes, setContactEditNotes] = useState('');
  const [pendingDeleteContactId, setPendingDeleteContactId] = useState<number | null>(null);
  const [pendingDeleteTemplateId, setPendingDeleteTemplateId] = useState<number | null>(null);

  // 管理者設定取得
  const { data: settingsData, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => adminService.getSettings(),
    enabled: isAdmin && activeTab === 'settings',
    select: (data) => (Array.isArray(data) ? data : []),
  });

  const settings = settingsData ?? [];

  // 会社情報取得
  const { data: companyInfoData, isLoading: isLoadingCompanyInfo } = useQuery({
    queryKey: ['admin', 'company-info'],
    queryFn: () => adminService.getCompanyInfo(),
    enabled: isAdmin && activeTab === 'company',
  });

  useEffect(() => {
    if (companyInfoData) {
      setCompanyInfo(companyInfoData);
    }
  }, [companyInfoData]);

  // メールテンプレート取得
  const { data: emailTemplates, isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['admin', 'email-templates'],
    queryFn: () => adminService.getEmailTemplates(),
    enabled: isAdmin && activeTab === 'email-templates',
    select: (data) => (Array.isArray(data) ? data : []),
  });

  // メール履歴取得
  const {
    data: emailHistoryData,
    isLoading: isLoadingHistory,
    isError: isEmailHistoryError,
    error: emailHistoryError,
    refetch: refetchEmailHistory,
  } = useQuery({
    queryKey: ['admin', 'email-history', emailHistoryPage],
    queryFn: () => adminService.getEmailHistory({ page: emailHistoryPage, limit: 50 }),
    enabled: isAdmin && activeTab === 'email-history',
    staleTime: 0,
  });

  // メール履歴: データ減少などで現在ページが総ページ数を超えたときにクランプ（空ページのままになりがちなため）
  useEffect(() => {
    if (activeTab !== 'email-history' || !emailHistoryData?.pagination) return;
    const totalPages = Math.max(1, Number(emailHistoryData.pagination.totalPages) || 1);
    if (emailHistoryPage > totalPages) {
      setEmailHistoryPage(totalPages);
    }
  }, [activeTab, emailHistoryData?.pagination, emailHistoryPage]);

  // お知らせ件数取得
  const {
    data: noticeCountsData,
    isLoading: isLoadingNoticeCounts,
    isError: isNoticeCountsError,
    error: noticeCountsQueryError,
    refetch: refetchNoticeCounts,
  } = useQuery({
    queryKey: ['admin', 'notice-counts'],
    // グローバル staleTime(5分) のままだと他画面で対応後も件数が古いままになりやすい
    staleTime: 0,
    queryFn: async () => {
      const [
        pendingAll,
        inProgressAll,
        pendingBug,
        inProgressBug,
        pendingFeature,
        inProgressFeature,
      ] = await Promise.all([
        contactService.list({ status: 'pending', limit: 1 }),
        contactService.list({ status: 'in_progress', limit: 1 }),
        contactService.list({ status: 'pending', subject: 'bug', limit: 1 }),
        contactService.list({ status: 'in_progress', subject: 'bug', limit: 1 }),
        contactService.list({ status: 'pending', subject: 'feature', limit: 1 }),
        contactService.list({ status: 'in_progress', subject: 'feature', limit: 1 }),
      ]);

      const [featureAll, featureBugFix] = await Promise.all([
        featureRequestService.list({ unanswered: true, limit: 1 }),
        featureRequestService.list({ unanswered: true, category: 'bug_fix', limit: 1 }),
      ]);

      const [emailPending, emailFailed] = await Promise.all([
        adminService.getEmailHistory({ status: 'pending', limit: 1 }),
        adminService.getEmailHistory({ status: 'failed', limit: 1 }),
      ]);

      const getTotal = (response?: { pagination?: { total?: unknown } } | null) => {
        const raw = response?.pagination?.total;
        if (raw == null) return 0;
        const n = Number(raw);
        return Number.isFinite(n) ? n : 0;
      };

      const pendingAllTotal = getTotal(pendingAll);
      const inProgressAllTotal = getTotal(inProgressAll);
      const pendingBugTotal = getTotal(pendingBug);
      const inProgressBugTotal = getTotal(inProgressBug);
      const pendingFeatureTotal = getTotal(pendingFeature);
      const inProgressFeatureTotal = getTotal(inProgressFeature);
      const featureAllTotal = getTotal(featureAll);
      const featureBugFixTotal = getTotal(featureBugFix);
      const emailPendingTotal = getTotal(emailPending);
      const emailFailedTotal = getTotal(emailFailed);

      const inquiries =
        pendingAllTotal +
        inProgressAllTotal -
        pendingBugTotal -
        inProgressBugTotal -
        pendingFeatureTotal -
        inProgressFeatureTotal;

      const errorReports = pendingBugTotal + inProgressBugTotal + featureBugFixTotal;

      const featureRequests =
        Math.max(0, featureAllTotal - featureBugFixTotal) +
        pendingFeatureTotal +
        inProgressFeatureTotal;

      const unreadEmails = emailPendingTotal + emailFailedTotal;

      return {
        inquiries: Math.max(0, inquiries),
        errorReports,
        featureRequests,
        unreadEmails,
      };
    },
    enabled: isAdmin && (activeTab === 'notice' || activeTab === 'contacts'),
  });

  // お問い合わせ一覧取得
  const {
    data: contactListData,
    isLoading: isLoadingContacts,
    isError: isContactsListError,
    error: contactsListError,
    refetch: refetchContactsList,
  } = useQuery({
    queryKey: ['admin', 'contacts', 'pending-in-progress'],
    staleTime: 0,
    queryFn: async () => {
      const [pendingRes, inProgressRes] = await Promise.all([
        contactService.list({ status: 'pending', limit: 20 }),
        contactService.list({ status: 'in_progress', limit: 20 }),
      ]);
      return {
        pending: pendingRes.data ?? [],
        inProgress: inProgressRes.data ?? [],
      };
    },
    enabled: isAdmin && activeTab === 'contacts',
  });

  const updateContactMutation = useMutation({
    mutationFn: ({
      id,
      status,
      adminNotes,
    }: {
      id: number;
      status: Contact['status'];
      adminNotes: string | null;
    }) => contactService.update(id, { status, adminNotes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'contacts'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'notice-counts'] });
      setShowContactModal(false);
      setEditingContact(null);
      setSuccess(t('admin.contacts_save_success', { defaultValue: '保存しました' }));
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message;
      setError(
        errorMessage || t('admin.contacts_save_failed', { defaultValue: '保存に失敗しました' })
      );
      setTimeout(() => setError(''), 5000);
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: (id: number) => contactService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'contacts'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'notice-counts'] });
      setShowContactModal(false);
      setEditingContact(null);
      setPendingDeleteContactId(null);
      setSuccess(t('admin.contacts_delete_success', { defaultValue: '削除しました' }));
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message;
      setError(
        errorMessage || t('admin.contacts_delete_failed', { defaultValue: '削除に失敗しました' })
      );
      setTimeout(() => setError(''), 5000);
    },
  });

  const resendEmailMutation = useMutation({
    mutationFn: (history: EmailHistory) =>
      adminService.sendEmail({
        templateId: history.templateId ?? undefined,
        templateKey: history.templateKey ?? undefined,
        to: history.to,
        cc: history.cc,
        bcc: history.bcc,
        subject: history.subject,
        body: history.body,
        language: history.language || 'ja',
        variables: history.variables,
      }),
    onSuccess: () => {
      setSuccess(t('admin.email_history_resend_success'));
      setShowHistoryModal(false);
      setSelectedHistory(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'email-history'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'notice-counts'] });
      setTimeout(() => setSuccess(''), 4000);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message;
      setError(errorMessage || t('admin.email_history_resend_error'));
      setTimeout(() => setError(''), 5000);
    },
  });

  // 設定保存
  const saveSettingMutation = useMutation({
    mutationFn: ({
      key,
      value,
      description,
      category,
    }: {
      key: string;
      value: string;
      description: string | null;
      category: string;
    }) => adminService.updateSetting(key, value, description, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setSuccess(t('admin.settings_saved'));
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message;
      setError(
        errorMessage?.startsWith('api.error.') || errorMessage?.startsWith('admin.')
          ? t(errorMessage, { defaultValue: errorMessage || t('admin.settings_save_failed') })
          : errorMessage || t('admin.settings_save_failed')
      );
      setTimeout(() => setError(''), 5000);
    },
  });

  // 新規設定追加
  const addSettingMutation = useMutation({
    mutationFn: ({
      key,
      value,
      description,
      category,
    }: {
      key: string;
      value: string;
      description: string;
      category: string;
    }) => adminService.updateSetting(key, value, description, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setNewSettingKey('');
      setNewSettingValue('');
      setNewSettingDescription('');
      setNewSettingCategory('general');
      setSuccess(t('admin.settings_added'));
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message;
      setError(
        errorMessage?.startsWith('api.error.') || errorMessage?.startsWith('admin.')
          ? t(errorMessage, { defaultValue: errorMessage || t('admin.settings_add_failed') })
          : errorMessage || t('admin.settings_add_failed')
      );
      setTimeout(() => setError(''), 5000);
    },
  });

  // 会社情報保存
  const saveCompanyInfoMutation = useMutation({
    mutationFn: (data: Partial<CompanyInfo>) => adminService.updateCompanyInfo(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'company-info'] });
      setSuccess(t('admin.company_info_saved'));
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message;
      setError(
        errorMessage?.startsWith('api.error.') || errorMessage?.startsWith('admin.')
          ? t(errorMessage, { defaultValue: errorMessage || t('admin.company_info_save_failed') })
          : errorMessage || t('admin.company_info_save_failed')
      );
      setTimeout(() => setError(''), 5000);
    },
  });

  // メールテンプレート保存
  const saveTemplateMutation = useMutation({
    mutationFn: ({ id, data }: { id?: number; data: Partial<EmailTemplate> }) => {
      if (id) {
        return adminService.updateEmailTemplate(id, data);
      } else {
        return adminService.createEmailTemplate(data as any);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'email-templates'] });
      setShowTemplateModal(false);
      setSelectedTemplate(null);
      setTemplateForm({});
      setSuccess(t('admin.email_templates_saved'));
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message;
      setError(
        errorMessage?.startsWith('api.error.') || errorMessage?.startsWith('admin.')
          ? t(errorMessage, {
              defaultValue: errorMessage || t('admin.email_templates_save_failed'),
            })
          : errorMessage || t('admin.email_templates_save_failed')
      );
      setTimeout(() => setError(''), 5000);
    },
  });

  // メールテンプレート削除
  const deleteTemplateMutation = useMutation({
    mutationFn: (id: number) => adminService.deleteEmailTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'email-templates'] });
      setPendingDeleteTemplateId(null);
      setSuccess(t('admin.email_templates_deleted'));
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message;
      setError(
        errorMessage?.startsWith('api.error.') || errorMessage?.startsWith('admin.')
          ? t(errorMessage, {
              defaultValue: errorMessage || t('admin.email_templates_delete_failed'),
            })
          : errorMessage || t('admin.email_templates_delete_failed')
      );
      setTimeout(() => setError(''), 5000);
    },
  });

  // テンプレート編集モーダルを開く
  const handleEditTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setTemplateForm(template);
    setSelectedLanguage('ja');
    setShowTemplateModal(true);
  };

  // 新規テンプレート作成
  const handleNewTemplate = () => {
    setSelectedTemplate(null);
    setTemplateForm({
      templateKey: '',
      name: '',
      description: '',
      subjectJa: '',
      bodyJa: '',
      category: 'general',
      isActive: true,
    });
    setSelectedLanguage('ja');
    setShowTemplateModal(true);
  };

  // テンプレート保存
  const handleSaveTemplate = () => {
    if (
      !templateForm.templateKey ||
      !templateForm.name ||
      !templateForm.subjectJa ||
      !templateForm.bodyJa
    ) {
      setError(t('admin.email_templates_required_fields'));
      setTimeout(() => setError(''), 5000);
      return;
    }
    saveTemplateMutation.mutate({
      id: selectedTemplate?.id,
      data: templateForm,
    });
  };

  // テンプレート複製
  const handleDuplicateTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(null);
    setTemplateForm({
      ...template,
      id: undefined,
      templateKey: `${template.templateKey}-copy`,
      name: `${template.name} (コピー)`,
      createdAt: undefined,
      updatedAt: undefined,
    });
    setSelectedLanguage('ja');
    setShowTemplateModal(true);
  };

  // 管理者でない場合はアクセス拒否
  if (!isAdmin) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>
            {t('admin.access_denied', { defaultValue: 'アクセス拒否' })}
          </Alert.Heading>
          <p>
            {t('admin.access_denied_message', {
              defaultValue: 'このページにアクセスするには管理者権限が必要です。',
            })}
          </p>
        </Alert>
      </Container>
    );
  }

  const dateFnsLocale = getDateFnsLocaleForAdmin(i18n.language);

  const noticeCounts = noticeCountsData || {
    inquiries: 0,
    errorReports: 0,
    featureRequests: 0,
    unreadEmails: 0,
  };

  const contactLists = contactListData || {
    pending: [],
    inProgress: [],
  };

  const getContactSubjectLabel = (subject?: Contact['subject']) => {
    const labels: Record<string, string> = {
      general: t('contact.subject_general'),
      technical: t('contact.subject_technical'),
      billing: t('contact.subject_billing'),
      bug: t('contact.subject_bug'),
      feature: t('contact.subject_feature'),
      other: t('contact.subject_other'),
    };
    return labels[subject || 'other'] || t('contact.subject_other');
  };

  const getContactStatusLabel = (status?: Contact['status']) => {
    const labels: Record<string, string> = {
      pending: t('admin.contacts_status_pending'),
      in_progress: t('admin.contacts_status_in_progress'),
      resolved: t('admin.contacts_status_resolved'),
      closed: t('admin.contacts_status_closed'),
    };
    return labels[status || 'pending'] || t('admin.contacts_status_pending');
  };

  const getContactStatusVariant = (status?: Contact['status']) => {
    switch (status) {
      case 'in_progress':
        return 'primary';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'secondary';
      default:
        return 'warning';
    }
  };

  const getContactDisplayName = (contact: Contact) => {
    if (contact.user) {
      return (
        formatUserName(contact.user.firstName, contact.user.lastName, i18n.language) ||
        contact.user.username ||
        contact.name ||
        '-'
      );
    }
    return contact.name || '-';
  };

  const getContactMessagePreview = (message?: string | null) => {
    if (!message) {
      return '-';
    }
    const normalized = message.replace(/\s+/g, ' ').trim();
    if (!normalized) {
      return '-';
    }
    return normalized.length > 120 ? `${normalized.slice(0, 120)}...` : normalized;
  };

  const openContactEdit = (contact: Contact) => {
    setEditingContact(contact);
    setContactEditStatus(contact.status);
    setContactEditNotes(contact.adminNotes ?? '');
    setShowContactModal(true);
  };

  // 他タブの useQuery が isLoading でも画面全体を覆わない（例: 設定表示中にメール履歴だけ再取得中など）
  const isCurrentTabLoading =
    (activeTab === 'settings' && isLoadingSettings) ||
    (activeTab === 'company' && isLoadingCompanyInfo) ||
    (activeTab === 'email-templates' && isLoadingTemplates) ||
    (activeTab === 'email-history' && isLoadingHistory) ||
    (activeTab === 'notice' && isLoadingNoticeCounts) ||
    (activeTab === 'contacts' && isLoadingContacts);

  if (isCurrentTabLoading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1>{t('admin.title')}</h1>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <CollapsibleHelpSection
        titleKey="admin.how_to_use_title"
        descriptionKey="admin.how_to_use_desc"
        stepsKey={[
          'admin.how_to_use_step1',
          'admin.how_to_use_step2',
          'admin.how_to_use_step3',
          'admin.how_to_use_step4',
        ]}
        storageKey="admin_settings"
        defaultExpanded={false}
      />

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'settings')} className="mb-4">
        {/* お知らせタブ */}
        {isAdmin && (
          <Tab eventKey="notice" title={t('admin.notice_tab', { defaultValue: 'お知らせ' })}>
          <Card className="mb-4">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{t('admin.notice_title', { defaultValue: '対応状況' })}</h5>
              </div>
            </Card.Header>
            <Card.Body>
              {isNoticeCountsError && (
                <Alert variant="warning" className="mb-3">
                  <div className="d-flex flex-wrap align-items-center gap-2 justify-content-between">
                    <span>
                      {t('admin.notice_counts_load_error', {
                        defaultValue:
                          '件数サマリーの取得に失敗しました。通信状況を確認のうえ、再試行してください。',
                      })}
                    </span>
                    <Button variant="outline-secondary" size="sm" onClick={() => void refetchNoticeCounts()}>
                      {t('common.retry')}
                    </Button>
                  </div>
                  {noticeCountsQueryError instanceof Error && noticeCountsQueryError.message ? (
                    <div className="small text-muted mt-2 mb-0">{noticeCountsQueryError.message}</div>
                  ) : null}
                </Alert>
              )}
              <Row className="g-3">
                <Col md={6} lg={3}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="fw-semibold">{t('admin.notice_inquiries')}</div>
                        <Badge bg={noticeCounts.inquiries > 0 ? 'danger' : 'secondary'}>
                          {t('admin.notice_count', { count: noticeCounts.inquiries })}
                        </Badge>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setActiveTab('contacts')}
                        >
                          {t('admin.notice_link_contacts')}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6} lg={3}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="fw-semibold">{t('admin.notice_error_reports')}</div>
                        <Badge bg={noticeCounts.errorReports > 0 ? 'danger' : 'secondary'}>
                          {t('admin.notice_count', { count: noticeCounts.errorReports })}
                        </Badge>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setActiveTab('contacts')}
                        >
                          {t('admin.notice_link_contact_bugs')}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6} lg={3}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="fw-semibold">{t('admin.notice_feature_requests')}</div>
                        <Badge bg={noticeCounts.featureRequests > 0 ? 'danger' : 'secondary'}>
                          {t('admin.notice_count', { count: noticeCounts.featureRequests })}
                        </Badge>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setActiveTab('contacts')}
                        >
                          {t('admin.notice_link_contact_features')}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6} lg={3}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="fw-semibold">{t('admin.notice_unread_emails')}</div>
                        <Badge bg={noticeCounts.unreadEmails > 0 ? 'danger' : 'secondary'}>
                          {t('admin.notice_count', { count: noticeCounts.unreadEmails })}
                        </Badge>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setActiveTab('email-history')}
                        >
                          {t('admin.notice_link_email_history')}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          </Tab>
        )}

        {/* お問い合わせタブ */}
        {isAdmin && (
          <Tab eventKey="contacts" title={t('admin.contacts_tab', { defaultValue: 'お問い合わせ' })}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">{t('admin.contacts_title')}</h5>
            </Card.Header>
            <Card.Body>
              {isContactsListError && (
                <Alert variant="warning" className="mb-3">
                  <div className="d-flex flex-wrap align-items-center gap-2 justify-content-between">
                    <span>
                      {t('admin.contacts_list_load_error', {
                        defaultValue:
                          'お問い合わせ一覧の取得に失敗しました。通信状況を確認のうえ、再試行してください。',
                      })}
                    </span>
                    <Button variant="outline-secondary" size="sm" onClick={() => void refetchContactsList()}>
                      {t('common.retry')}
                    </Button>
                  </div>
                  {contactsListError instanceof Error && contactsListError.message ? (
                    <div className="small text-muted mt-2 mb-0">{contactsListError.message}</div>
                  ) : null}
                </Alert>
              )}
              <Alert variant="info" className="mb-0">
                {t('admin.contacts_description')}
              </Alert>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">{t('admin.contacts_pending_title')}</h6>
                <Badge bg={contactLists.pending.length > 0 ? 'warning' : 'secondary'}>
                  {t('admin.notice_count', { count: contactLists.pending.length })}
                </Badge>
              </div>
            </Card.Header>
            <Card.Body>
              {contactLists.pending.length === 0 ? (
                <Alert variant="light" className="mb-0">
                  {t('admin.contacts_empty')}
                </Alert>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>{t('admin.contacts_table_status')}</th>
                      <th>{t('admin.contacts_table_subject')}</th>
                      <th>{t('admin.contacts_table_name')}</th>
                      <th>{t('admin.contacts_table_email')}</th>
                      <th>{t('admin.contacts_table_message')}</th>
                      <th>{t('admin.contacts_table_created_at')}</th>
                      <th>{t('admin.contacts_table_actions', { defaultValue: '操作' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactLists.pending.map((contact) => (
                      <tr key={`pending-${contact.id}`}>
                        <td>
                          <Badge bg={getContactStatusVariant(contact.status)}>
                            {getContactStatusLabel(contact.status)}
                          </Badge>
                        </td>
                        <td>{getContactSubjectLabel(contact.subject)}</td>
                        <td>{getContactDisplayName(contact)}</td>
                        <td>{contact.email}</td>
                        <td style={{ maxWidth: '240px' }}>{getContactMessagePreview(contact.message)}</td>
                        <td>
                          {format(new Date(contact.createdAt), 'yyyy/MM/dd HH:mm', { locale: dateFnsLocale })}
                        </td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <Button variant="primary" size="sm" onClick={() => openContactEdit(contact)}>
                              {t('admin.contacts_edit', { defaultValue: '編集' })}
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={deleteContactMutation.isPending}
                              onClick={() => setPendingDeleteContactId(contact.id)}
                            >
                              {t('admin.contacts_delete', { defaultValue: '削除' })}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">{t('admin.contacts_in_progress_title')}</h6>
                <Badge bg={contactLists.inProgress.length > 0 ? 'primary' : 'secondary'}>
                  {t('admin.notice_count', { count: contactLists.inProgress.length })}
                </Badge>
              </div>
            </Card.Header>
            <Card.Body>
              {contactLists.inProgress.length === 0 ? (
                <Alert variant="light" className="mb-0">
                  {t('admin.contacts_empty')}
                </Alert>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>{t('admin.contacts_table_status')}</th>
                      <th>{t('admin.contacts_table_subject')}</th>
                      <th>{t('admin.contacts_table_name')}</th>
                      <th>{t('admin.contacts_table_email')}</th>
                      <th>{t('admin.contacts_table_message')}</th>
                      <th>{t('admin.contacts_table_created_at')}</th>
                      <th>{t('admin.contacts_table_actions', { defaultValue: '操作' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactLists.inProgress.map((contact) => (
                      <tr key={`in-progress-${contact.id}`}>
                        <td>
                          <Badge bg={getContactStatusVariant(contact.status)}>
                            {getContactStatusLabel(contact.status)}
                          </Badge>
                        </td>
                        <td>{getContactSubjectLabel(contact.subject)}</td>
                        <td>{getContactDisplayName(contact)}</td>
                        <td>{contact.email}</td>
                        <td style={{ maxWidth: '240px' }}>{getContactMessagePreview(contact.message)}</td>
                        <td>
                          {format(new Date(contact.createdAt), 'yyyy/MM/dd HH:mm', { locale: dateFnsLocale })}
                        </td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <Button variant="primary" size="sm" onClick={() => openContactEdit(contact)}>
                              {t('admin.contacts_edit', { defaultValue: '編集' })}
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={deleteContactMutation.isPending}
                              onClick={() => setPendingDeleteContactId(contact.id)}
                            >
                              {t('admin.contacts_delete', { defaultValue: '削除' })}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
          </Tab>
        )}

        {/* 管理者設定タブ */}
        <Tab eventKey="settings" title={t('admin.settings_tab', { defaultValue: '管理者設定' })}>
          {/* 管理者メール通知のオン/オフ */}
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">{t('admin.mail_notification_title', { defaultValue: '管理者メール通知' })}</h6>
            </Card.Header>
            <Card.Body>
              <Form.Group className="d-flex align-items-center gap-2">
                <Form.Check
                  type="switch"
                  id="mail-admin-notification-enabled"
                  checked={(() => {
                    const s = settings.find((x) => x.key === 'mail_admin_notification_enabled');
                    if (!s) return true;
                    const v = (s.value || '').toLowerCase().trim();
                    return v !== 'false' && v !== '0' && v !== 'off';
                  })()}
                  onChange={(e) => {
                    const value = e.target.checked ? 'true' : 'false';
                    saveSettingMutation.mutate({
                      key: 'mail_admin_notification_enabled',
                      value,
                      description: t('admin.mail_notification_description', {
                        defaultValue: 'サンプル登録・ユーザー登録・支払い結果等の管理者向け通知メールを送信するか',
                      }),
                      category: 'mail',
                    });
                  }}
                  disabled={saveSettingMutation.isPending}
                />
                <Form.Label htmlFor="mail-admin-notification-enabled" className="mb-0">
                  {t('admin.mail_notification_label', { defaultValue: '管理者へ通知メールを送信する' })}
                </Form.Label>
              </Form.Group>
              <Form.Text className="text-muted small">
                {t('admin.mail_notification_hint', {
                  defaultValue: 'オフにすると、サンプル登録・更新・削除、新規ユーザー登録、支払い結果などの管理者通知メールは送信されません。送信先は環境変数 MAIL_ADMIN で指定します。',
                })}
              </Form.Text>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{t('admin.settings_title')}</h5>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setNewSettingKey('');
                    setNewSettingValue('');
                    setNewSettingDescription('');
                    setNewSettingCategory('general');
                  }}
                >
                  {t('admin.settings_add_new')}
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {/* 新規設定フォーム */}
              <Card className="mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                <Card.Body>
                  <h6>{t('admin.settings_add_new')}</h6>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('admin.settings_key')}</Form.Label>
                        <Form.Control
                          type="text"
                          value={newSettingKey}
                          onChange={(e) => setNewSettingKey(e.target.value)}
                          placeholder={t('admin.settings_key_placeholder', {
                            defaultValue: '例: SITE_NAME',
                          })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('admin.settings_category')}</Form.Label>
                        <Form.Select
                          value={newSettingCategory}
                          onChange={(e) => setNewSettingCategory(e.target.value)}
                        >
                          <option value="general">{t('admin.settings_category_general')}</option>
                          <option value="mail">{t('admin.settings_category_mail')}</option>
                          <option value="system">{t('admin.settings_category_system')}</option>
                          <option value="security">{t('admin.settings_category_security')}</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('admin.settings_value')}</Form.Label>
                        <Form.Control
                          type="text"
                          value={newSettingValue}
                          onChange={(e) => setNewSettingValue(e.target.value)}
                          placeholder={t('admin.settings_value_placeholder', {
                            defaultValue: '設定値',
                          })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('admin.settings_description')}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={newSettingDescription}
                      onChange={(e) => setNewSettingDescription(e.target.value)}
                      placeholder={t('admin.settings_description_placeholder', {
                        defaultValue: '設定の説明（任意）',
                      })}
                    />
                  </Form.Group>
                  <Button
                    variant="success"
                    onClick={() => {
                      if (!newSettingKey || !newSettingValue) {
                        setError(t('admin.settings_key_required'));
                        setTimeout(() => setError(''), 5000);
                        return;
                      }
                      addSettingMutation.mutate({
                        key: newSettingKey,
                        value: newSettingValue,
                        description: newSettingDescription,
                        category: newSettingCategory,
                      });
                    }}
                    disabled={addSettingMutation.isPending}
                  >
                    {addSettingMutation.isPending
                      ? t('common.processing')
                      : t('admin.settings_add')}
                  </Button>
                </Card.Body>
              </Card>

              {/* 既存設定一覧 */}
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>{t('admin.settings_key')}</th>
                    <th>{t('admin.settings_category')}</th>
                    <th>{t('admin.settings_value')}</th>
                    <th>{t('admin.settings_description')}</th>
                    <th>{t('admin.settings_updated_at')}</th>
                    <th>{t('admin.settings_action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {settings.map((setting) => (
                    <SettingRow
                      key={setting.id}
                      setting={setting}
                      onSave={(key, value, description, category) => {
                        saveSettingMutation.mutate({ key, value, description, category });
                      }}
                    />
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        {/* 会社情報タブ */}
        <Tab eventKey="company" title={t('admin.company_tab')}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">{t('admin.company_info_title')}</h5>
            </Card.Header>
            <Card.Body>
              <CompanyInfoForm
                companyInfo={companyInfo}
                onChange={setCompanyInfo}
                onSave={() => {
                  saveCompanyInfoMutation.mutate(companyInfo);
                }}
                isSaving={saveCompanyInfoMutation.isPending}
              />
            </Card.Body>
          </Card>
        </Tab>

        {/* メールテンプレートタブ */}
        <Tab eventKey="email-templates" title={t('admin.email_templates_tab')}>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{t('admin.email_templates_title')}</h5>
                <Button variant="primary" size="sm" onClick={handleNewTemplate}>
                  {t('admin.email_templates_create')}
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>
                      {t('admin.email_templates_search', { defaultValue: '検索' })}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t('admin.email_templates_search_placeholder', {
                        defaultValue: 'テンプレートキー、名前で検索...',
                      })}
                      value={emailTemplateSearchQuery}
                      onChange={(e) => setEmailTemplateSearchQuery(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      {t('admin.email_templates_category_filter', { defaultValue: 'カテゴリ' })}
                    </Form.Label>
                    <Form.Select
                      value={emailTemplateCategoryFilter || 'all'}
                      onChange={(e) =>
                        setEmailTemplateCategoryFilter(
                          e.target.value === 'all' ? null : e.target.value
                        )
                      }
                    >
                      <option value="all">
                        {t('admin.email_templates_category_all', {
                          defaultValue: 'すべてのカテゴリ',
                        })}
                      </option>
                      <option value="reservation">
                        {t('admin.email_templates_category_reservation', {
                          defaultValue: '予約システム',
                        })}
                      </option>
                      <option value="auth">
                        {t('admin.email_templates_category_auth', {
                          defaultValue: '認証・ユーザー',
                        })}
                      </option>
                      <option value="user">
                        {t('admin.email_templates_category_user', { defaultValue: 'ユーザー管理' })}
                      </option>
                      <option value="billing">
                        {t('admin.email_templates_category_billing', {
                          defaultValue: '請求・課金',
                        })}
                      </option>
                      <option value="notification">
                        {t('admin.email_templates_category_notification', { defaultValue: '通知' })}
                      </option>
                      <option value="system">
                        {t('admin.email_templates_category_system', { defaultValue: 'システム' })}
                      </option>
                      <option value="general">
                        {t('admin.email_templates_category_general', { defaultValue: '一般' })}
                      </option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      {t('admin.email_templates_status_filter', { defaultValue: 'ステータス' })}
                    </Form.Label>
                    <Form.Select
                      value={emailTemplateStatusFilter || 'all'}
                      onChange={(e) =>
                        setEmailTemplateStatusFilter(
                          e.target.value === 'all' ? null : e.target.value
                        )
                      }
                    >
                      <option value="all">
                        {t('admin.email_templates_status_all', { defaultValue: 'すべて' })}
                      </option>
                      <option value="active">
                        {t('admin.email_templates_status_active', { defaultValue: '有効' })}
                      </option>
                      <option value="inactive">
                        {t('admin.email_templates_status_inactive', { defaultValue: '無効' })}
                      </option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setEmailTemplateSearchQuery('');
                      setEmailTemplateCategoryFilter(null);
                      setEmailTemplateStatusFilter(null);
                    }}
                  >
                    {t('common.clear', { defaultValue: 'クリア' })}
                  </Button>
                </Col>
              </Row>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>{t('admin.email_templates_key')}</th>
                    <th>{t('admin.email_templates_name')}</th>
                    <th>{t('admin.email_templates_category')}</th>
                    <th>{t('admin.email_templates_status')}</th>
                    <th>{t('admin.email_templates_updated_at')}</th>
                    <th>{t('admin.email_templates_action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(emailTemplates ?? [])
                    .filter((template) => {
                      // カテゴリフィルタ
                      if (
                        emailTemplateCategoryFilter &&
                        template.category !== emailTemplateCategoryFilter
                      ) {
                        return false;
                      }
                      // ステータスフィルタ
                      if (emailTemplateStatusFilter) {
                        if (emailTemplateStatusFilter === 'active' && !template.isActive) {
                          return false;
                        }
                        if (emailTemplateStatusFilter === 'inactive' && template.isActive) {
                          return false;
                        }
                      }
                      // 検索クエリフィルタ
                      if (emailTemplateSearchQuery) {
                        const query = emailTemplateSearchQuery.toLowerCase();
                        const matchesKey = template.templateKey?.toLowerCase().includes(query);
                        const matchesName = template.name?.toLowerCase().includes(query);
                        const matchesDescription = template.description
                          ?.toLowerCase()
                          .includes(query);
                        if (!matchesKey && !matchesName && !matchesDescription) {
                          return false;
                        }
                      }
                      return true;
                    })
                    .map((template) => (
                      <tr key={template.id}>
                        <td>{template.templateKey}</td>
                        <td>{template.name}</td>
                        <td>{template.category}</td>
                        <td>
                          <Badge bg={template.isActive ? 'success' : 'secondary'}>
                            {template.isActive
                              ? t('admin.email_templates_status_active')
                              : t('admin.email_templates_status_inactive')}
                          </Badge>
                        </td>
                        <td>
                          {format(new Date(template.updatedAt), 'yyyy/MM/dd HH:mm', { locale: dateFnsLocale })}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="info"
                              size="sm"
                              onClick={() => {
                                setPreviewTemplate(template);
                                setShowPreviewModal(true);
                              }}
                              title={t('admin.email_templates_preview', {
                                defaultValue: 'プレビュー',
                              })}
                            >
                              {t('admin.email_templates_preview', { defaultValue: 'プレビュー' })}
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleDuplicateTemplate(template)}
                              title={t('admin.email_templates_duplicate', { defaultValue: '複製' })}
                            >
                              {t('admin.email_templates_duplicate', { defaultValue: '複製' })}
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleEditTemplate(template)}
                            >
                              {t('admin.email_templates_edit')}
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => setPendingDeleteTemplateId(template.id)}
                            >
                              {t('admin.email_templates_delete')}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        {/* メール送信履歴タブ */}
        <Tab eventKey="email-history" title={t('admin.email_history_tab')}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">{t('admin.email_history_title')}</h5>
            </Card.Header>
            <Card.Body>
              {isEmailHistoryError && (
                <Alert variant="warning" className="mb-3">
                  <div className="d-flex flex-wrap align-items-center gap-2 justify-content-between">
                    <span>
                      {t('admin.email_history_load_error', {
                        defaultValue:
                          'メール送信履歴の取得に失敗しました。通信状況を確認のうえ、再試行してください。',
                      })}
                    </span>
                    <Button variant="outline-secondary" size="sm" onClick={() => void refetchEmailHistory()}>
                      {t('common.retry')}
                    </Button>
                  </div>
                  {emailHistoryError instanceof Error && emailHistoryError.message ? (
                    <div className="small text-muted mt-2 mb-0">{emailHistoryError.message}</div>
                  ) : null}
                </Alert>
              )}
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>{t('admin.email_history_sent_at')}</th>
                    <th>{t('admin.email_history_to')}</th>
                    <th>{t('admin.email_history_subject')}</th>
                    <th>{t('admin.email_history_template')}</th>
                    <th>{t('admin.email_history_language')}</th>
                    <th>{t('admin.email_history_status')}</th>
                    <th>{t('admin.email_history_sender')}</th>
                    <th>{t('admin.email_history_action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(emailHistoryData?.data ?? []).map((history) => (
                    <tr key={history.id}>
                      <td>
                        {format(new Date(history.sentAt), 'yyyy/MM/dd HH:mm', { locale: dateFnsLocale })}
                      </td>
                      <td>{history.to}</td>
                      <td>{history.subject}</td>
                      <td>{history.template?.name || history.templateKey || '-'}</td>
                      <td>{history.language}</td>
                      <td>
                        <Badge
                          bg={
                            history.status === 'sent'
                              ? 'success'
                              : history.status === 'failed'
                                ? 'danger'
                                : 'warning'
                          }
                        >
                          {history.status === 'sent'
                            ? t('admin.email_history_status_sent')
                            : history.status === 'failed'
                              ? t('admin.email_history_status_failed')
                              : t('admin.email_history_status_pending')}
                        </Badge>
                      </td>
                      <td>
                        {history.sender
                          ? formatUserName(
                              history.sender.firstName,
                              history.sender.lastName,
                              i18n.language
                            ) ||
                            history.sender.username ||
                            '-'
                          : '-'}
                      </td>
                      <td>
                        <Button
                          variant="info"
                          size="sm"
                          onClick={() => {
                            setSelectedHistory(history);
                            setShowHistoryModal(true);
                          }}
                        >
                          {t('admin.email_history_detail')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {emailHistoryData?.pagination && (() => {
                const total = Number(emailHistoryData.pagination.total) || 0;
                const page = Number(emailHistoryData.pagination.page) || 1;
                const limit = Number(emailHistoryData.pagination.limit) || 50;
                const totalPages = Math.max(1, Number(emailHistoryData.pagination.totalPages) || 1);
                const start = total === 0 ? 0 : (page - 1) * limit + 1;
                const end = total === 0 ? 0 : Math.min(page * limit, total);
                return (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      {total === 0
                        ? t('admin.email_history_pagination_zero', { defaultValue: '0件' })
                        : t('admin.email_history_pagination_total', {
                            total,
                            start,
                            end,
                          })}
                    </div>
                    <div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setEmailHistoryPage(emailHistoryPage - 1)}
                      >
                        {t('admin.email_history_pagination_prev')}
                      </Button>
                      <span className="mx-2">
                        {t('admin.email_history_pagination_page', {
                          page,
                          totalPages,
                        })}
                      </span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={page >= totalPages || total === 0}
                        onClick={() => setEmailHistoryPage(emailHistoryPage + 1)}
                      >
                        {t('admin.email_history_pagination_next')}
                      </Button>
                    </div>
                  </div>
                );
              })()}
            </Card.Body>
          </Card>
        </Tab>

        {/* システム統計タブ */}
        <Tab
          eventKey="system-stats"
          title={t('admin.system_stats_tab', { defaultValue: 'システム統計' })}
        >
          <SystemStatsTab queryEnabled={activeTab === 'system-stats'} />
        </Tab>
      </Tabs>

      {/* メールテンプレート編集モーダル */}
      <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedTemplate
              ? t('admin.email_templates_modal_title_edit')
              : t('admin.email_templates_modal_title_create')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EmailTemplateForm
            template={templateForm}
            onChange={setTemplateForm}
            selectedLanguage={selectedLanguage}
            onLanguageChange={setSelectedLanguage}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>
            {t('admin.email_templates_modal_cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveTemplate}
            disabled={saveTemplateMutation.isPending}
          >
            {saveTemplateMutation.isPending
              ? t('admin.email_templates_modal_saving')
              : t('admin.email_templates_modal_save')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* メールテンプレートプレビューモーダル */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {t('admin.email_templates_preview_title', {
              defaultValue: 'メールテンプレートプレビュー',
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewTemplate && (
            <div>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>{t('admin.email_templates_key')}:</strong>
                </Col>
                <Col md={8}>{previewTemplate.templateKey}</Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>{t('admin.email_templates_name')}:</strong>
                </Col>
                <Col md={8}>{previewTemplate.name}</Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>{t('admin.email_templates_category')}:</strong>
                </Col>
                <Col md={8}>{previewTemplate.category}</Col>
              </Row>
              {previewTemplate.description && (
                <Row className="mb-3">
                  <Col md={4}>
                    <strong>{t('admin.email_templates_modal_description')}:</strong>
                  </Col>
                  <Col md={8}>{previewTemplate.description}</Col>
                </Row>
              )}
              {previewTemplate.variables && typeof previewTemplate.variables === 'object' && (
                <Row className="mb-3">
                  <Col md={4}>
                    <strong>
                      {t('admin.email_templates_variables', { defaultValue: '使用可能な変数' })}:
                    </strong>
                  </Col>
                  <Col md={8}>
                    <div className="d-flex flex-wrap gap-2">
                      {Object.entries(previewTemplate.variables as Record<string, string>).map(
                        ([key, value]) => (
                          <Badge key={key} bg="info" className="mb-1">
                            {`{{${key}}}`} - {value}
                          </Badge>
                        )
                      )}
                    </div>
                  </Col>
                </Row>
              )}
              <hr />
              <h6>
                {t('admin.email_templates_preview_content', { defaultValue: 'プレビュー内容' })}
              </h6>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.email_templates_modal_language_select')}</Form.Label>
                <Form.Select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                >
                  {getLanguages(t).map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  <strong>{t('admin.email_templates_modal_subject')}:</strong>
                </Form.Label>
                <Form.Control
                  type="text"
                  readOnly
                  value={
                    (previewTemplate[
                      `subject${selectedLanguage === 'ja' ? 'Ja' : selectedLanguage === 'en' ? 'En' : selectedLanguage === 'zh-CN' ? 'ZhCn' : selectedLanguage === 'fr' ? 'Fr' : selectedLanguage === 'es' ? 'Es' : selectedLanguage === 'de' ? 'De' : selectedLanguage === 'pt' ? 'Pt' : 'Ru'}` as keyof EmailTemplate
                    ] as string) ||
                    previewTemplate.subjectJa ||
                    '-'
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  <strong>{t('admin.email_templates_modal_body')}:</strong>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  readOnly
                  value={
                    (previewTemplate[
                      `body${selectedLanguage === 'ja' ? 'Ja' : selectedLanguage === 'en' ? 'En' : selectedLanguage === 'zh-CN' ? 'ZhCn' : selectedLanguage === 'fr' ? 'Fr' : selectedLanguage === 'es' ? 'Es' : selectedLanguage === 'de' ? 'De' : selectedLanguage === 'pt' ? 'Pt' : 'Ru'}` as keyof EmailTemplate
                    ] as string) ||
                    previewTemplate.bodyJa ||
                    '-'
                  }
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            {t('common.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* お問い合わせ編集モーダル */}
      <Modal
        show={showContactModal}
        onHide={() => {
          setShowContactModal(false);
          setEditingContact(null);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('admin.contacts_edit_modal_title', {
              defaultValue: 'お問い合わせの編集',
            })}
            {editingContact ? ` #${editingContact.id}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingContact && (
            <div>
              <Row className="mb-2">
                <Col md={3} className="text-muted small">
                  {t('admin.contacts_table_name')}
                </Col>
                <Col md={9}>{getContactDisplayName(editingContact)}</Col>
              </Row>
              <Row className="mb-2">
                <Col md={3} className="text-muted small">
                  {t('admin.contacts_table_email')}
                </Col>
                <Col md={9}>{editingContact.email}</Col>
              </Row>
              <Row className="mb-2">
                <Col md={3} className="text-muted small">
                  {t('admin.contacts_table_subject')}
                </Col>
                <Col md={9}>{getContactSubjectLabel(editingContact.subject)}</Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.contacts_modal_message', { defaultValue: '本文' })}</Form.Label>
                <Form.Control as="textarea" rows={8} readOnly value={editingContact.message} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.contacts_table_status')}</Form.Label>
                <Form.Select
                  value={contactEditStatus}
                  onChange={(e) =>
                    setContactEditStatus(e.target.value as Contact['status'])
                  }
                >
                  <option value="pending">{t('admin.contacts_status_pending')}</option>
                  <option value="in_progress">{t('admin.contacts_status_in_progress')}</option>
                  <option value="resolved">{t('admin.contacts_status_resolved')}</option>
                  <option value="closed">{t('admin.contacts_status_closed')}</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>
                  {t('admin.contacts_admin_notes', { defaultValue: '管理者メモ' })}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={contactEditNotes}
                  onChange={(e) => setContactEditNotes(e.target.value)}
                  placeholder={t('admin.contacts_admin_notes_placeholder', {
                    defaultValue: '社内用メモ（任意）',
                  })}
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            className="me-auto"
            disabled={!editingContact || deleteContactMutation.isPending}
            onClick={() => {
              if (editingContact) setPendingDeleteContactId(editingContact.id);
            }}
          >
            {t('admin.contacts_delete', { defaultValue: '削除' })}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setShowContactModal(false);
              setEditingContact(null);
            }}
          >
            {t('admin.settings_cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={!editingContact || updateContactMutation.isPending}
            onClick={() => {
              if (!editingContact) return;
              updateContactMutation.mutate({
                id: editingContact.id,
                status: contactEditStatus,
                adminNotes: contactEditNotes.trim() ? contactEditNotes.trim() : null,
              });
            }}
          >
            {updateContactMutation.isPending
              ? t('common.processing')
              : t('admin.contacts_save', { defaultValue: '保存' })}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* メール履歴詳細モーダル */}
      <Modal
        show={showHistoryModal}
        onHide={() => {
          setShowHistoryModal(false);
          setSelectedHistory(null);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.email_history_modal_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedHistory && (
            <div>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>{t('admin.email_history_modal_sent_at')}:</strong>
                </Col>
                <Col md={8}>
                  {format(new Date(selectedHistory.sentAt), t('admin.email_history_date_format'), {
                    locale: dateFnsLocale,
                  })}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>{t('admin.email_history_modal_to')}:</strong>
                </Col>
                <Col md={8}>{selectedHistory.to}</Col>
              </Row>
              {selectedHistory.cc && (
                <Row className="mb-3">
                  <Col md={4}>
                    <strong>{t('admin.email_history_modal_cc')}:</strong>
                  </Col>
                  <Col md={8}>{selectedHistory.cc}</Col>
                </Row>
              )}
              {selectedHistory.bcc && (
                <Row className="mb-3">
                  <Col md={4}>
                    <strong>{t('admin.email_history_modal_bcc')}:</strong>
                  </Col>
                  <Col md={8}>{selectedHistory.bcc}</Col>
                </Row>
              )}
              <Row className="mb-3">
                <Col md={4}>
                  <strong>{t('admin.email_history_modal_subject')}:</strong>
                </Col>
                <Col md={8}>{selectedHistory.subject}</Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>{t('admin.email_history_modal_template')}:</strong>
                </Col>
                <Col md={8}>
                  {selectedHistory.template?.name || selectedHistory.templateKey || '-'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>{t('admin.email_history_modal_language')}:</strong>
                </Col>
                <Col md={8}>{selectedHistory.language}</Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>{t('admin.email_history_modal_status')}:</strong>
                </Col>
                <Col md={8}>
                  <Badge
                    bg={
                      selectedHistory.status === 'sent'
                        ? 'success'
                        : selectedHistory.status === 'failed'
                          ? 'danger'
                          : 'warning'
                    }
                  >
                    {selectedHistory.status === 'sent'
                      ? t('admin.email_history_status_sent')
                      : selectedHistory.status === 'failed'
                        ? t('admin.email_history_status_failed')
                        : t('admin.email_history_status_pending')}
                  </Badge>
                </Col>
              </Row>
              {selectedHistory.errorMessage && (
                <Row className="mb-3">
                  <Col md={4}>
                    <strong>{t('admin.email_history_modal_error_message')}:</strong>
                  </Col>
                  <Col md={8}>
                    <Alert variant="danger">{selectedHistory.errorMessage}</Alert>
                  </Col>
                </Row>
              )}
              <Row className="mb-3">
                <Col md={12}>
                  <strong>{t('admin.email_history_modal_body')}:</strong>
                  <div
                    style={{
                      border: '1px solid #dee2e6',
                      padding: '1rem',
                      marginTop: '0.5rem',
                      borderRadius: '0.25rem',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {selectedHistory.body}
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex flex-wrap justify-content-between gap-2">
          <div className="d-flex gap-2">
            {selectedHistory &&
              (selectedHistory.status === 'failed' || selectedHistory.status === 'pending') && (
                <Button
                  variant="warning"
                  disabled={resendEmailMutation.isPending}
                  onClick={() => {
                    if (selectedHistory) {
                      void resendEmailMutation.mutateAsync(selectedHistory);
                    }
                  }}
                >
                  {resendEmailMutation.isPending
                    ? t('common.processing')
                    : t('admin.email_history_resend')}
                </Button>
              )}
            {selectedHistory?.status === 'sent' && (
              <Button
                variant="outline-secondary"
                disabled={resendEmailMutation.isPending}
                onClick={() => {
                  if (
                    selectedHistory &&
                    window.confirm(t('admin.email_history_resend_sent_confirm'))
                  ) {
                    void resendEmailMutation.mutateAsync(selectedHistory);
                  }
                }}
              >
                {t('admin.email_history_resend_duplicate')}
              </Button>
            )}
          </div>
          <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
            {t('admin.email_history_modal_close')}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmDestructiveModal
        show={pendingDeleteContactId !== null}
        onHide={() => setPendingDeleteContactId(null)}
        message={t('admin.contacts_delete_confirm', {
          defaultValue: 'このお問い合わせを削除しますか？',
        })}
        isLoading={deleteContactMutation.isPending}
        onConfirm={async () => {
          if (pendingDeleteContactId !== null) {
            await deleteContactMutation.mutateAsync(pendingDeleteContactId);
          }
        }}
      />
      <ConfirmDestructiveModal
        show={pendingDeleteTemplateId !== null}
        onHide={() => setPendingDeleteTemplateId(null)}
        message={t('admin.email_templates_delete_confirm')}
        isLoading={deleteTemplateMutation.isPending}
        onConfirm={async () => {
          if (pendingDeleteTemplateId !== null) {
            await deleteTemplateMutation.mutateAsync(pendingDeleteTemplateId);
          }
        }}
      />
    </Container>
  );
}

// 設定行コンポーネント
function SettingRow({
  setting,
  onSave,
}: {
  setting: AdminSetting;
  onSave: (key: string, value: string, description: string | null, category: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocaleForAdmin(i18n.language);
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(setting.value);
  const [description, setDescription] = useState(setting.description || '');
  const [category, setCategory] = useState(setting.category);

  const handleSave = () => {
    onSave(setting.key, value, description || null, category);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <tr>
        <td>{setting.key}</td>
        <td>
          <Form.Select value={category} onChange={(e) => setCategory(e.target.value)} size="sm">
            <option value="general">{t('admin.settings_category_general')}</option>
            <option value="mail">{t('admin.settings_category_mail')}</option>
            <option value="system">{t('admin.settings_category_system')}</option>
            <option value="security">{t('admin.settings_category_security')}</option>
          </Form.Select>
        </td>
        <td>
          <Form.Control
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            size="sm"
          />
        </td>
        <td>
          <Form.Control
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="sm"
          />
        </td>
        <td>{format(new Date(setting.updatedAt), 'yyyy/MM/dd HH:mm', { locale: dateFnsLocale })}</td>
        <td>
          <Button variant="success" size="sm" className="me-2" onClick={handleSave}>
            {t('admin.settings_save')}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setIsEditing(false)}>
            {t('admin.settings_cancel')}
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{setting.key}</td>
      <td>{setting.category}</td>
      <td>{setting.value}</td>
      <td>{setting.description || '-'}</td>
      <td>{format(new Date(setting.updatedAt), 'yyyy/MM/dd HH:mm', { locale: dateFnsLocale })}</td>
      <td>
        <Button variant="primary" size="sm" onClick={() => setIsEditing(true)}>
          {t('admin.settings_edit')}
        </Button>
      </td>
    </tr>
  );
}

// 会社情報フォームコンポーネント
function CompanyInfoForm({
  companyInfo,
  onChange,
  onSave,
  isSaving,
}: {
  companyInfo: Partial<CompanyInfo>;
  onChange: (info: Partial<CompanyInfo>) => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Form>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              {t('admin.company_info_name_ja')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={companyInfo.name || ''}
              onChange={(e) => onChange({ ...companyInfo, name: e.target.value })}
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.company_info_name_en')}</Form.Label>
            <Form.Control
              type="text"
              value={companyInfo.nameEn || ''}
              onChange={(e) => onChange({ ...companyInfo, nameEn: e.target.value || null })}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.company_info_postal_code')}</Form.Label>
            <Form.Control
              type="text"
              value={companyInfo.postalCode || ''}
              onChange={(e) => onChange({ ...companyInfo, postalCode: e.target.value || null })}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.company_info_prefecture')}</Form.Label>
            <Form.Control
              type="text"
              value={companyInfo.prefecture || ''}
              onChange={(e) => onChange({ ...companyInfo, prefecture: e.target.value || null })}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.company_info_city')}</Form.Label>
            <Form.Control
              type="text"
              value={companyInfo.city || ''}
              onChange={(e) => onChange({ ...companyInfo, city: e.target.value || null })}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.company_info_address_line1')}</Form.Label>
            <Form.Control
              type="text"
              value={companyInfo.addressLine1 || ''}
              onChange={(e) => onChange({ ...companyInfo, addressLine1: e.target.value || null })}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.company_info_address_line2')}</Form.Label>
            <Form.Control
              type="text"
              value={companyInfo.addressLine2 || ''}
              onChange={(e) => onChange({ ...companyInfo, addressLine2: e.target.value || null })}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.company_info_phone')}</Form.Label>
            <Form.Control
              type="tel"
              value={companyInfo.phone || ''}
              onChange={(e) => onChange({ ...companyInfo, phone: e.target.value || null })}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.company_info_email')}</Form.Label>
            <Form.Control
              type="email"
              value={companyInfo.email || ''}
              onChange={(e) => onChange({ ...companyInfo, email: e.target.value || null })}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.company_info_website')}</Form.Label>
            <Form.Control
              type="url"
              value={companyInfo.website || ''}
              onChange={(e) => onChange({ ...companyInfo, website: e.target.value || null })}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.company_info_invoice_reg_number')}</Form.Label>
            <Form.Control
              type="text"
              value={companyInfo.invoiceRegNumber || ''}
              onChange={(e) =>
                onChange({ ...companyInfo, invoiceRegNumber: e.target.value || null })
              }
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.company_info_logo_url')}</Form.Label>
            <Form.Control
              type="url"
              value={companyInfo.logoUrl || ''}
              onChange={(e) => onChange({ ...companyInfo, logoUrl: e.target.value || null })}
            />
            <Form.Text className="text-muted">
              {t('admin.company_info_logo_url_note', {
                defaultValue: '任意: 公開ページや帳票に利用できます。',
              })}
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              {t('admin.company_info_seal_url', { defaultValue: '印鑑画像URL' })}
            </Form.Label>
            <Form.Control
              type="url"
              value={companyInfo.sealUrl || ''}
              onChange={(e) => onChange({ ...companyInfo, sealUrl: e.target.value || null })}
            />
            <Form.Text className="text-muted">
              {t('admin.company_info_seal_url_note', {
                defaultValue: '書類PDFに印鑑を表示します（PNG推奨）。',
              })}
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.company_info_description_ja')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={companyInfo.description || ''}
              onChange={(e) => onChange({ ...companyInfo, description: e.target.value || null })}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.company_info_description_en')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={companyInfo.descriptionEn || ''}
              onChange={(e) => onChange({ ...companyInfo, descriptionEn: e.target.value || null })}
            />
          </Form.Group>
        </Col>
      </Row>
      <Button variant="primary" onClick={onSave} disabled={isSaving || !companyInfo.name}>
        {isSaving ? t('admin.company_info_saving') : t('admin.company_info_save')}
      </Button>
    </Form>
  );
}

// メールテンプレートフォームコンポーネント
function EmailTemplateForm({
  template,
  onChange,
  selectedLanguage,
  onLanguageChange,
}: {
  template: Partial<EmailTemplate>;
  onChange: (template: Partial<EmailTemplate>) => void;
  selectedLanguage: string;
  onLanguageChange: (lang: string) => void;
}) {
  const getSubjectKey = (lang: string) => {
    const keyMap: { [key: string]: keyof EmailTemplate } = {
      ja: 'subjectJa',
      en: 'subjectEn',
      'zh-CN': 'subjectZhCn',
      fr: 'subjectFr',
      es: 'subjectEs',
      de: 'subjectDe',
      pt: 'subjectPt',
      ru: 'subjectRu',
    };
    return keyMap[lang] || 'subjectJa';
  };

  const getBodyKey = (lang: string) => {
    const keyMap: { [key: string]: keyof EmailTemplate } = {
      ja: 'bodyJa',
      en: 'bodyEn',
      'zh-CN': 'bodyZhCn',
      fr: 'bodyFr',
      es: 'bodyEs',
      de: 'bodyDe',
      pt: 'bodyPt',
      ru: 'bodyRu',
    };
    return keyMap[lang] || 'bodyJa';
  };

  const { t } = useTranslation();
  const languages = getLanguages(t);
  const currentLangName = languages.find((l) => l.code === selectedLanguage)?.name || '';

  return (
    <div>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              {t('admin.email_templates_modal_key')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={template.templateKey || ''}
              onChange={(e) => onChange({ ...template, templateKey: e.target.value })}
              placeholder={t('admin.email_templates_modal_key_placeholder', {
                defaultValue: '例: password_reset',
              })}
              disabled={!!template.id}
            />
            <Form.Text className="text-muted">
              {t('admin.email_templates_modal_key_hint')}
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              {t('admin.email_templates_modal_name')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={template.name || ''}
              onChange={(e) => onChange({ ...template, name: e.target.value })}
              placeholder={t('admin.email_templates_modal_name_placeholder', {
                defaultValue: '例: パスワードリセット',
              })}
            />
          </Form.Group>
        </Col>
      </Row>
      <Form.Group className="mb-3">
        <Form.Label>{t('admin.email_templates_modal_description')}</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={template.description || ''}
          onChange={(e) => onChange({ ...template, description: e.target.value || null })}
        />
      </Form.Group>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.email_templates_modal_category')}</Form.Label>
            <Form.Select
              value={template.category || 'general'}
              onChange={(e) => onChange({ ...template, category: e.target.value })}
            >
              <option value="general">
                {t('admin.email_templates_modal_category_general', { defaultValue: '一般' })}
              </option>
              <option value="reservation">
                {t('admin.email_templates_modal_category_reservation', {
                  defaultValue: '予約システム',
                })}
              </option>
              <option value="auth">
                {t('admin.email_templates_modal_category_auth', { defaultValue: '認証・ユーザー' })}
              </option>
              <option value="user">
                {t('admin.email_templates_modal_category_user', { defaultValue: 'ユーザー管理' })}
              </option>
              <option value="billing">
                {t('admin.email_templates_modal_category_billing', { defaultValue: '請求・課金' })}
              </option>
              <option value="notification">
                {t('admin.email_templates_modal_category_notification', { defaultValue: '通知' })}
              </option>
              <option value="system">
                {t('admin.email_templates_modal_category_system', { defaultValue: 'システム' })}
              </option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.email_templates_modal_status')}</Form.Label>
            <Form.Select
              value={template.isActive ? 'true' : 'false'}
              onChange={(e) => onChange({ ...template, isActive: e.target.value === 'true' })}
            >
              <option value="true">{t('admin.email_templates_modal_status_active')}</option>
              <option value="false">{t('admin.email_templates_modal_status_inactive')}</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <hr />
      <h6>{t('admin.email_templates_modal_multilang_title')}</h6>
      <Form.Group className="mb-3">
        <Form.Label>{t('admin.email_templates_modal_language_select')}</Form.Label>
        <Form.Select value={selectedLanguage} onChange={(e) => onLanguageChange(e.target.value)}>
          {languages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Alert variant="info" className="mb-3">
        <strong>{t('admin.email_templates_modal_language_note')}</strong>{' '}
        {selectedLanguage === 'ja'
          ? t('admin.email_templates_modal_language_editing')
          : t('admin.email_templates_modal_language_not_edited')}
        {selectedLanguage === 'ja' &&
          ` ${t('admin.email_templates_modal_language_note_required', { defaultValue: '日本語の件名と本文は必須です。' })}`}
      </Alert>
      <Form.Group className="mb-3">
        <Form.Label>
          {t('admin.email_templates_modal_subject')} ({currentLangName}){' '}
          {selectedLanguage === 'ja' && <span className="text-danger">*</span>}
        </Form.Label>
        <Form.Control
          type="text"
          value={(template[getSubjectKey(selectedLanguage)] as string) || ''}
          onChange={(e) => {
            const key = getSubjectKey(selectedLanguage);
            onChange({ ...template, [key]: e.target.value || null });
          }}
          placeholder={
            selectedLanguage === 'ja'
              ? t('admin.email_templates_modal_subject_placeholder_ja')
              : t('admin.email_templates_modal_subject_placeholder_other')
          }
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>
          {t('admin.email_templates_modal_body')} ({currentLangName}){' '}
          {selectedLanguage === 'ja' && <span className="text-danger">*</span>}
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={10}
          value={(template[getBodyKey(selectedLanguage)] as string) || ''}
          onChange={(e) => {
            const key = getBodyKey(selectedLanguage);
            onChange({ ...template, [key]: e.target.value || null });
          }}
          placeholder={
            selectedLanguage === 'ja'
              ? t('admin.email_templates_modal_body_placeholder_ja')
              : t('admin.email_templates_modal_body_placeholder_other')
          }
        />
        <Form.Text className="text-muted">
          {t('admin.email_templates_modal_variables_hint')}
        </Form.Text>
      </Form.Group>
      {template.variables && typeof template.variables === 'object' && (
        <Form.Group className="mb-3">
          <Form.Label>
            <strong>
              {t('admin.email_templates_variables', { defaultValue: '使用可能な変数' })}:
            </strong>
          </Form.Label>
          <div className="p-3 bg-light rounded">
            <div className="d-flex flex-wrap gap-2">
              {Object.entries(template.variables as Record<string, string>).map(([key, value]) => (
                <Badge key={key} bg="info" className="mb-1">
                  {`{{${key}}}`} - {value}
                </Badge>
              ))}
            </div>
          </div>
        </Form.Group>
      )}
    </div>
  );
}

// システム統計タブコンポーネント
function SystemStatsTab({ queryEnabled }: { queryEnabled: boolean }) {
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocaleForAdmin(i18n.language);
  const {
    data: stats,
    isLoading,
    isError,
    error: systemStatsError,
    refetch: refetchSystemStats,
  } = useQuery({
    queryKey: ['admin', 'system-stats'],
    queryFn: () => adminService.getSystemStats(),
    enabled: queryEnabled,
    staleTime: 0,
  });

  const exportSettingsMutation = useMutation({
    mutationFn: () => adminService.exportSettings(),
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `admin-settings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      alert(t('admin.settings_exported', { defaultValue: '設定をエクスポートしました' }));
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message ||
        t('admin.settings_export_failed', { defaultValue: 'エクスポートに失敗しました' });
      alert(
        errorMessage?.startsWith('api.error.') || errorMessage?.startsWith('admin.')
          ? t(errorMessage, { defaultValue: errorMessage })
          : errorMessage
      );
    },
  });

  const [importFile, setImportFile] = useState<File | null>(null);
  const importSettingsMutation = useMutation({
    mutationFn: (data: any) => adminService.importSettings(data),
    onSuccess: () => {
      alert(t('admin.settings_imported', { defaultValue: '設定をインポートしました' }));
      setImportFile(null);
      window.location.reload();
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message ||
        t('admin.settings_import_failed', { defaultValue: 'インポートに失敗しました' });
      alert(
        errorMessage?.startsWith('api.error.') || errorMessage?.startsWith('admin.')
          ? t(errorMessage, { defaultValue: errorMessage })
          : errorMessage
      );
    },
  });

  const handleImport = () => {
    if (!importFile) {
      alert(
        t('admin.settings_import_file_required', { defaultValue: 'ファイルを選択してください' })
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        importSettingsMutation.mutate(data);
      } catch (error) {
        alert(t('admin.settings_import_invalid_file', { defaultValue: '無効なファイル形式です' }));
      }
    };
    reader.readAsText(importFile);
  };

  if (queryEnabled && isLoading) {
    return (
      <div className="text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </Spinner>
      </div>
    );
  }

  if (queryEnabled && isError) {
    return (
      <Alert variant="warning">
        <div className="d-flex flex-wrap align-items-center gap-2 justify-content-between">
          <span>
            {t('admin.system_stats_load_error', {
              defaultValue:
                'システム統計の取得に失敗しました。通信状況を確認のうえ、再試行してください。',
            })}
          </span>
          <Button variant="outline-secondary" size="sm" onClick={() => void refetchSystemStats()}>
            {t('common.retry')}
          </Button>
        </div>
        {systemStatsError instanceof Error && systemStatsError.message ? (
          <div className="small text-muted mt-2 mb-0">{systemStatsError.message}</div>
        ) : null}
      </Alert>
    );
  }

  return (
    <div>
      <Row>
        <Col md={3}>
          <Card className="mb-3">
            <Card.Body>
              <h6>{t('admin.system_stats_total_users', { defaultValue: '総ユーザー数' })}</h6>
              <h3>{stats?.totalUsers || 0}</h3>
              <small className="text-muted">
                {t('admin.system_stats_active_users', { defaultValue: 'アクティブ' })}:{' '}
                {stats?.activeUsers || 0}
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="mb-3">
            <Card.Body>
              <h6>{t('admin.system_stats_total_samples', { defaultValue: '総サンプル数' })}</h6>
              <h3>{stats?.totalSamples || 0}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="mb-3">
            <Card.Body>
              <h6>{t('admin.system_stats_total_storage_rooms', { defaultValue: '総保管庫数' })}</h6>
              <h3>{stats?.totalStorageRooms || 0}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Body>
              <h6>
                {t('admin.system_stats_email_templates', { defaultValue: 'メールテンプレート数' })}
              </h6>
              <h3>{stats?.totalEmailTemplates || 0}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Body>
              <h6>{t('admin.system_stats_email_history', { defaultValue: 'メール送信履歴数' })}</h6>
              <h3>{stats?.totalEmailHistory || 0}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">
            {t('admin.system_stats_recent_access', { defaultValue: '最近のアクセスログ' })}
          </h5>
        </Card.Header>
        <Card.Body>
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>{t('admin.system_stats_access_time', { defaultValue: 'アクセス日時' })}</th>
                <th>{t('admin.system_stats_user', { defaultValue: 'ユーザー' })}</th>
                <th>{t('admin.system_stats_action', { defaultValue: 'アクション' })}</th>
                <th>{t('admin.system_stats_ip_address', { defaultValue: 'IPアドレス' })}</th>
                <th>{t('admin.system_stats_access_type', { defaultValue: 'アクセスタイプ' })}</th>
              </tr>
            </thead>
            <tbody>
              {stats?.recentAccessLogs?.map((log: any) => (
                <tr key={log.id}>
                  <td>
                    {format(new Date(log.createdAt || log.accessTime), 'yyyy/MM/dd HH:mm:ss', {
                      locale: dateFnsLocale,
                    })}
                  </td>
                  <td>{log.user ? `${log.user.username || log.user.email || '-'}` : '-'}</td>
                  <td>{log.action || '-'}</td>
                  <td>{log.ipAddress || '-'}</td>
                  <td>
                    <Badge bg={log.isPublicAccess ? 'info' : 'secondary'}>
                      {log.isPublicAccess
                        ? t('admin.system_stats_public', { defaultValue: '公開' })
                        : t('admin.system_stats_private', { defaultValue: '非公開' })}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            {t('admin.settings_export_import', { defaultValue: '設定のエクスポート/インポート' })}
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <h6>{t('admin.settings_export', { defaultValue: '設定をエクスポート' })}</h6>
              <p className="text-muted">
                {t('admin.settings_export_desc', {
                  defaultValue: '現在の設定をJSONファイルとしてダウンロードします。',
                })}
              </p>
              <Button
                variant="primary"
                onClick={() => exportSettingsMutation.mutate()}
                disabled={exportSettingsMutation.isPending}
              >
                {exportSettingsMutation.isPending
                  ? t('common.processing')
                  : t('admin.settings_export_button', { defaultValue: 'エクスポート' })}
              </Button>
            </Col>
            <Col md={6}>
              <h6>{t('admin.settings_import', { defaultValue: '設定をインポート' })}</h6>
              <p className="text-muted">
                {t('admin.settings_import_desc', {
                  defaultValue: 'JSONファイルから設定を復元します。',
                })}
              </p>
              <Form.Group className="mb-3">
                <Form.Control
                  type="file"
                  accept=".json"
                  onChange={(e) => setImportFile((e.target as HTMLInputElement).files?.[0] || null)}
                />
              </Form.Group>
              <Button
                variant="success"
                onClick={handleImport}
                disabled={importSettingsMutation.isPending || !importFile}
              >
                {importSettingsMutation.isPending
                  ? t('common.processing')
                  : t('admin.settings_import_button', { defaultValue: 'インポート' })}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
}
